import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Search } from "@mui/icons-material";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { AgGridReact } from "ag-grid-react";

import { SEARCH_KEY, SEARCH_SCOPE_FOLDER_ID_KEY, SEARCH_SCOPE_KEY } from "fond/constants";
import { useContents } from "fond/hooks/useContents";
import { useScopedSearchData } from "fond/hooks/useScopedSearchData";
import { SearchScope } from "fond/topBar/useSearchScope";
import { Folder, Project, Report } from "fond/types";
import { GridId } from "fond/types/grids";
import { searchCollectionByKey } from "fond/utils/search";
import { AgGrid, BlockSpinner, NonIdealState } from "fond/widgets";

import { EntityMenu } from "../ItemMenu/EntityMenu";
import useProjectsTable from "../useProjectsTable";

import SearchResultScopeSelector from "./SearchResultScopeSelector";

interface NoResultsOverlayProps {
  isNonWorkspaceScope: boolean;
  onTryWorkspace: () => void;
}

const NoResultsOverlay: React.FC<NoResultsOverlayProps> = ({ isNonWorkspaceScope, onTryWorkspace }) => {
  if (!isNonWorkspaceScope) {
    return <span>No results found</span>;
  }

  return (
    <span style={{ pointerEvents: "auto" }}>
      No results found. Try searching the{" "}
      <Button variant="text" size="small" sx={{ textTransform: "none", p: 0, minWidth: "auto", verticalAlign: "baseline" }} onClick={onTryWorkspace}>
        Workspace
      </Button>
    </span>
  );
};

/**
 * Builds the /search URL with the given search query and scope.
 */
function buildSearchUrl(query: string, scope: SearchScope): string {
  const params = new URLSearchParams();
  params.set(SEARCH_KEY, query);
  params.set(SEARCH_SCOPE_KEY, scope.type);
  if (scope.folderId) {
    params.set(SEARCH_SCOPE_FOLDER_ID_KEY, scope.folderId);
  }
  return `/search?${params.toString()}`;
}

interface SearchResultContainerProps {
  searchQuery: string;
  scope: SearchScope;
}

const SearchResultContainer: React.FC<SearchResultContainerProps> = ({ searchQuery, scope }) => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>(undefined);
  const [menuEntity, setMenuEntity] = useState<Folder | Project | Report | undefined>(undefined);
  const [inputValue, setInputValue] = useState(searchQuery);

  // Keep input in sync when URL-driven searchQuery changes (e.g. browser back/forward)
  const prevSearchQuery = useRef(searchQuery);
  if (prevSearchQuery.current !== searchQuery) {
    prevSearchQuery.current = searchQuery;
    setInputValue(searchQuery);
  }

  const { isLoading, isFetching } = useContents();

  const { projects: scopedProjects, folders: scopedFolders, reports: scopedReports, scopeFolderName, isScopeLoading } = useScopedSearchData(scope);

  // Apply text search on the scoped collections
  const rowData = useMemo(() => {
    const projects = searchCollectionByKey(scopedProjects, searchQuery, "ProjectName");
    const folders = searchCollectionByKey(scopedFolders, searchQuery, "Name");
    const reports = searchCollectionByKey(scopedReports, searchQuery, "Name");
    return [...folders, ...projects, ...reports];
  }, [scopedProjects, scopedFolders, scopedReports, searchQuery]);

  const handleScopeChange = useCallback(
    (newScope: SearchScope) => {
      navigate(buildSearchUrl(searchQuery, newScope), { replace: true });
    },
    [searchQuery, navigate]
  );

  const handleSearchSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed && trimmed !== searchQuery) {
        navigate(buildSearchUrl(trimmed, scope), { replace: !searchQuery });
      }
    },
    [inputValue, searchQuery, scope, navigate]
  );

  const openRowMenu = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, entity?: Folder | Project) => {
    setMenuAnchor(e.currentTarget);
    setMenuEntity(entity);
  }, []);

  const closeRowMenu = () => setMenuAnchor(undefined);

  const { columns: gridColumns } = useProjectsTable({ openRowMenu });

  const gridOptions = useMemo(
    () => ({
      defaultColDef: {
        resizable: true,
        suppressMovable: true,
        sortable: true,
      },
      domLayout: "autoHeight" as const,
      animateRows: true,
      suppressGroupRowsSticky: true,
      sideBar: false,
      rowGroupPanelShow: "never" as const,
    }),
    []
  );

  const workspaceScope: SearchScope = useMemo(() => ({ type: "workspace", folderId: null, folderName: null }), []);

  const handleTryWorkspace = useCallback(() => {
    handleScopeChange(workspaceScope);
  }, [handleScopeChange, workspaceScope]);

  const noRowsOverlayParams = useMemo(
    () => ({
      isNonWorkspaceScope: scope.type !== "workspace",
      onTryWorkspace: handleTryWorkspace,
    }),
    [scope.type, handleTryWorkspace]
  );

  const hasInput = inputValue.trim() !== "";
  const hasChanges = hasInput && inputValue.trim() !== searchQuery;
  const hasActiveSearch = searchQuery !== "";

  if (isLoading || isScopeLoading) {
    return <BlockSpinner />;
  }

  return (
    <>
      <Box component="form" onSubmit={handleSearchSubmit}>
        <TextField
          autoFocus={!hasActiveSearch}
          fullWidth
          variant="outlined"
          placeholder="Search for anything you have access to..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          InputProps={{
            sx: {
              height: 44,
              fontSize: "0.9375rem",
              borderRadius: 2,
              pr: 0.5,
            },
            startAdornment: (
              <InputAdornment position="start" sx={{ gap: 0.5 }}>
                <Search sx={{ color: "action.active" }} />
                <SearchResultScopeSelector scope={scope} scopeFolderName={scopeFolderName} onScopeChange={handleScopeChange} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button type="submit" variant="contained" size="small" disabled={!hasChanges} sx={{ textTransform: "none", mr: 0.5 }}>
                  Search
                </Button>
              </InputAdornment>
            ),
          }}
          data-testid="search-results-search-input"
        />
      </Box>
      {hasActiveSearch ? (
        <Box data-testid="search-results-all" mt={2}>
          <AgGrid
            id={GridId.SEARCH}
            ref={gridRef}
            gridOptions={gridOptions}
            rowData={rowData}
            columnDefs={gridColumns}
            loading={isFetching}
            size="large"
            variant="outlined"
            noRowsOverlayComponent={NoResultsOverlay}
            noRowsOverlayComponentParams={noRowsOverlayParams}
          />
        </Box>
      ) : (
        <Box mt={6}>
          <NonIdealState
            icon={<Search sx={{ fontSize: "4em", color: "grey.300" }} />}
            description="Enter a search term above to find projects, folders, and reports."
          />
        </Box>
      )}
      {(menuAnchor || menuEntity) && <EntityMenu entity={menuEntity} anchorEl={menuAnchor} onMenuClose={closeRowMenu} source="search-results-list" />}
    </>
  );
};
export default SearchResultContainer;
